import { useCallback } from 'react';

import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';

import { formatPhoneNumberToE164 } from '@ocx/utils-phone-number';
import { OtpChannel } from '@ocx/graphql';
import { useConfiguration } from '@ocx/data-configuration';

import { RequestMagicLinkParams, RequestOtpParams } from '../../signup.types';
import { SignupPhoneNumberFormFields } from './signup-phone-number-form.types';
import { useShowAttemptTimeoutMessage } from '../../use-show-attempt-timeout-message.hook';

type UseSignupPhoneNumberFormControllerParams = {
  isBlockedByTimeout: boolean;
  handleRequestMagicLink(params: RequestMagicLinkParams): Promise<boolean>;
  handleRequestOTP(params: RequestOtpParams): Promise<boolean>;
};

export const useSignupPhoneNumberFormController = (params: UseSignupPhoneNumberFormControllerParams) => {
  const { triggerEvent } = useRudderStack();
  const showAttemptTimeoutMessage = useShowAttemptTimeoutMessage();

  const {
    primaryMethod,
    otp: { enabled: isOTPEnabled },
  } = useConfiguration('auth');
  const { isBlockedByTimeout, handleRequestMagicLink, handleRequestOTP } = params;

  const handleSubmit = useCallback(
    async (values: SignupPhoneNumberFormFields) => {
      if (isBlockedByTimeout) {
        showAttemptTimeoutMessage();
        return;
      }
      triggerEvent('sign_in_page_phone_number_submitted');

      const phoneNumber = formatPhoneNumberToE164(values.phoneNumber);
      if (primaryMethod === 'OTP' && isOTPEnabled) {
        await handleRequestOTP({ phoneNumber, otpChannel: OtpChannel.Sms, referralCode: values.referralCode });
      } else {
        await handleRequestMagicLink({ phoneNumber, referralCode: values.referralCode });
      }
    },
    [
      isBlockedByTimeout,
      triggerEvent,
      primaryMethod,
      isOTPEnabled,
      showAttemptTimeoutMessage,
      handleRequestOTP,
      handleRequestMagicLink,
    ],
  );

  return { handleSubmit, isLoading: false };
};
